import { useQuery } from '@apollo/client';
import { Card } from '@ovComponents/2-component/card/card';
import { Chip } from '@ovComponents/2-component/chip/chip';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { useMemo } from 'react';
import { getSubAccountId } from '../hooks/reviewRebalance/computeStagedTrades';
import {
  filterActiveTrades,
  useConflictKeys,
} from '../hooks/reviewRebalance/useConflictKeys';
import { useCreateRebalanceTrades } from '../hooks/reviewRebalance/useCreateRebalanceTrades';
import { usePortfolioReasons } from '../hooks/reviewRebalance/usePortfolioReasons';
import { useStagedTrades } from '../hooks/reviewRebalance/useStagedTrades';
import { useTradeStatsByCurrency } from '../hooks/reviewRebalance/useTradeStatsByCurrency';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { FETCH_SUB_TRADE_REQUESTS_FULL } from '../queries';
import {
  BORDERED_BOX_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
} from '../styles';
import type { PortfolioReport } from '../types';
import {
  calculateDrift,
  formatMoney,
  getPortfolioName,
  getPortfolioStatus,
} from '../utils';
import { isConflictingTrade } from '../utils/reviewRebalanceCalculations';

type ReviewAndCommitProps = {
  selectedPortfolios: PortfolioReport[];
  organizationId: string;
  bulkTradingProcess?: string;
  onTradesCreated: () => void;
  onCancel: () => void;
};

const getCreateButtonLabel = ({
  pendingSTRLoading,
  conflictCheckUnavailable,
  creatableTradeCount,
}: {
  pendingSTRLoading: boolean;
  conflictCheckUnavailable: boolean;
  creatableTradeCount: number;
}): string => {
  if (pendingSTRLoading) {
    return 'Checking for conflicts...';
  }
  if (conflictCheckUnavailable) {
    return 'Conflict check unavailable';
  }
  return `Create ${creatableTradeCount} trade${creatableTradeCount === 1 ? '' : 's'}`;
};

const ReviewAndCommit = ({
  selectedPortfolios,
  organizationId,
  bulkTradingProcess,
  onTradesCreated,
  onCancel,
}: ReviewAndCommitProps) => {
  const formatMoneyInActiveCurrency = useFormatMoney();
  const theme = useTheme();

  const { trades } = useStagedTrades(selectedPortfolios);

  const selectedSubAccountIds = useMemo(
    () => new Set(selectedPortfolios.map(getSubAccountId)),
    [selectedPortfolios],
  );

  const {
    data: pendingSTRData,
    error: pendingSTRError,
    loading: pendingSTRLoading,
  } = useQuery(FETCH_SUB_TRADE_REQUESTS_FULL, {
    variables: {
      input: {
        filter: { organizationId, states: ['INITIATED', 'READY'] },
        pagination: {
          perPage: 500,
          offSet: 0,
          sortField: 'createdAt',
          sortDesc: true,
        },
      },
    },
    skip: !organizationId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const conflictCheckUnavailable = Boolean(pendingSTRError) || !organizationId;
  const creationBlocked = pendingSTRLoading || conflictCheckUnavailable;

  const pendingSTRs = useMemo(
    () => pendingSTRData?.fetchSubTradeRequests?.subTradeRequests || [],
    [pendingSTRData],
  );

  const conflictKeys = useConflictKeys(
    trades,
    pendingSTRs,
    selectedSubAccountIds,
  );
  const activeTrades = useMemo(
    () => filterActiveTrades(trades, conflictKeys),
    [trades, conflictKeys],
  );
  const stats = useTradeStatsByCurrency(activeTrades);
  const portfolioReasons = usePortfolioReasons(selectedPortfolios);
  const { createTrades, creating } = useCreateRebalanceTrades({
    organizationId,
    bulkTradingProcess,
    onTradesCreated,
  });

  const creatableTradeCount = activeTrades.length;
  const skippedStagedTradeCount = useMemo(
    () =>
      trades.filter(
        (trade) => !trade.removed && isConflictingTrade(trade, conflictKeys),
      ).length,
    [trades, conflictKeys],
  );

  const summary = useMemo(() => {
    let totalMarketValue = 0;
    let totalCashAvailable = 0;
    selectedPortfolios.forEach((portfolio) => {
      totalMarketValue += portfolio.marketValueCents || 0;
      totalCashAvailable += portfolio.cashAvailableForTradeCents || 0;
    });
    return { totalMarketValue, totalCashAvailable };
  }, [selectedPortfolios]);

  const SUMMARY_CARDS = [
    {
      label: 'Total Market Value',
      value: formatMoneyInActiveCurrency(summary.totalMarketValue, {
        compact: true,
      }),
    },
    {
      label: 'Cash Available',
      value: formatMoneyInActiveCurrency(summary.totalCashAvailable, {
        compact: true,
      }),
    },
    {
      label: 'Portfolios',
      value: selectedPortfolios.length,
    },
    {
      label: 'Trades to Create',
      value: creatableTradeCount,
      subtext:
        skippedStagedTradeCount > 0
          ? `${skippedStagedTradeCount} skipped due to conflicts`
          : undefined,
    },
  ];

  const handleCreate = async () => {
    if (creatableTradeCount === 0 || creationBlocked || creating) return;
    await createTrades(activeTrades);
  };

  return (
    <Card
      sx={{
        border: '2px solid',
        borderColor: theme.color.primary,
      }}
    >
      <Box
        sx={{
          p: 3,
        }}
      >
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Typography variant='titleLarge' sx={{ mb: 0.5 }}>
            Review & Commit: Rebalance
          </Typography>
          <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
            {selectedPortfolios.length} portfolio
            {selectedPortfolios.length !== 1 ? 's' : ''} selected
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1.5,
            mb: 3,
          }}
        >
          {SUMMARY_CARDS.map(({ label, value, subtext }) => (
            <Box
              key={label}
              sx={{
                p: 1.5,
                ...BORDERED_BOX_SX,
                bgcolor: theme.color.background,
                borderRadius: 1,
              }}
            >
              <Typography
                variant='bodySmall'
                component='div'
                sx={{ ...SECTION_HEADER_SX, fontWeight: 600, mb: 0.5 }}
              >
                {label}
              </Typography>
              <Typography variant='titleMedium'>{value}</Typography>
              {subtext != null && (
                <Typography
                  variant='bodySmall'
                  component='div'
                  sx={SECONDARY_TEXT_SX}
                >
                  {subtext}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {conflictCheckUnavailable && !pendingSTRLoading && (
          <Box
            sx={{
              mb: 3,
            }}
          >
            <Typography
              variant='bodySmall'
              sx={{ color: theme.color.negative }}
            >
              Unable to verify conflicting trades — creation is disabled until
              this is resolved.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mb: 3,
          }}
        >
          <Typography variant='titleMedium' sx={{ mb: 1 }}>
            Selected Portfolios
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
            {selectedPortfolios.map((portfolio) => {
              const drift = calculateDrift(portfolio);
              const status = getPortfolioStatus(portfolio);
              const reason = portfolioReasons.get(portfolio.id)?.reason;
              return (
                <Box
                  key={portfolio.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    borderBottom: '1px solid',
                    borderColor: theme.color.outlineVariant,
                    '&:hover': { bgcolor: theme.color.neutralHover },
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                    }}
                  >
                    <Typography variant='bodySmall'>
                      {getPortfolioName(portfolio)}
                    </Typography>
                    {reason && (
                      <Typography
                        variant='bodySmall'
                        component='div'
                        sx={SECONDARY_TEXT_SX}
                      >
                        {reason}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant='bodySmall'>
                      {formatMoneyInActiveCurrency(portfolio.marketValueCents, {
                        compact: true,
                      })}
                    </Typography>
                    {drift && (
                      <Typography
                        variant='bodySmall'
                        sx={{
                          color:
                            drift > 5
                              ? theme.color.negative
                              : theme.color.onSurfaceVariant,
                        }}
                      >
                        {drift.toFixed(1)}% drift
                      </Typography>
                    )}
                    <Chip text={status.label} small />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {stats.totalsByCurrency.length > 0 && (
          <Box
            sx={{
              mb: 3,
            }}
          >
            <Typography variant='titleMedium' sx={{ mb: 1 }}>
              Trade Summary by Currency
            </Typography>
            {stats.totalsByCurrency.map((row) => (
              <Typography
                key={row.currency}
                variant='bodySmall'
                component='div'
                sx={{ mb: 0.5 }}
              >
                {row.currency}: Buy{' '}
                {formatMoney(row.buyCents, { currency: row.currency })} / Sell{' '}
                {formatMoney(row.sellCents, { currency: row.currency })} / Net{' '}
                {formatMoney(row.netCents, { currency: row.currency })}
              </Typography>
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button variant='outlined' size='small' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant='contained'
            size='small'
            onClick={handleCreate}
            disabled={creatableTradeCount === 0 || creating || creationBlocked}
          >
            {getCreateButtonLabel({
              pendingSTRLoading,
              conflictCheckUnavailable,
              creatableTradeCount,
            })}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default ReviewAndCommit;
